import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { PlayerAPI } from '@elfsight/widget-media-video';
import { useOnInView } from 'react-intersection-observer';
import {
  useVideoAutoplay,
  VISIBILITY_THRESHOLD
} from '../../../video-autoplay-provider';

interface UseVideoIntersectionParams {
  videoId: string;
  index: number;
  isInPopup: boolean;
  videoRef: RefObject<PlayerAPI>;
  canUseAutoplay: boolean;
  isHovered: boolean;
}

export function useVideoIntersection({
  videoId,
  index,
  isInPopup,
  videoRef,
  canUseAutoplay,
  isHovered
}: UseVideoIntersectionParams) {
  const { register, unregister, onVisibilityChange, onHover, onHoverEnd } =
    useVideoAutoplay();
  const [hasBeenInView, setHasBeenInView] = useState(false);

  const onViewRef = useOnInView(
    (inView, entry) => {
      // Отслеживаем, когда видео впервые появляется во viewport
      if (inView && !hasBeenInView) {
        setHasBeenInView(true);
      }

      const { intersectionRatio } = entry;

      // Отслеживание изменения видимости
      onVisibilityChange(videoId, entry.intersectionRatio);

      // Регистрация/отмена регистрации видео при изменении видимости
      if (intersectionRatio >= VISIBILITY_THRESHOLD) {
        register({
          id: videoId,
          index,
          isInPopup,
          wasCalculated: false,
          visibility: intersectionRatio,
          ref: videoRef.current
        });
      } else {
        unregister(videoId);
      }
    },
    { threshold: VISIBILITY_THRESHOLD }
  );

  const videoIntersectionRef = useCallback(
    (node: Element | null | undefined) => {
      if (node) {
        onViewRef(node);
      }
    },
    [onViewRef]
  );

  // Отслеживание hover для autoplay
  useEffect(() => {
    if (!canUseAutoplay || isInPopup) return;

    if (isHovered) {
      onHover(videoId);
    } else {
      onHoverEnd(videoId);
    }
  }, [isHovered, videoId, canUseAutoplay, isInPopup, onHover, onHoverEnd]);

  return {
    videoIntersectionRef,
    hasBeenInView
  };
}
